/**
 * FaceDetectionCamera - Integrated with existing face verification backend
 * =======================================================================
 * Uses existing server endpoints:
 * - POST /api/attendance/login (with selfie)
 * - POST /api/attendance/logout (with selfie)
 * - POST /api/attendance/break/start (with selfie)
 * - POST /api/attendance/break/end (with selfie)
 * 
 * Server uses:
 * - face-api.js with encrypted face_encoding stored in DB
 * - profile_photo / profile_image as master reference
 * - TinyFaceDetector for fast detection
 */

import React, { useRef, useEffect, useState, useCallback } from 'react';
import Webcam from 'react-webcam';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Typography,
  Chip,
  Alert,
} from '@mui/material';
import { Fingerprint } from '@mui/icons-material';
import { detectFaceInCanvas } from '../utils/faceDetector';

const DETECT_INTERVAL_MS = 150;

const FaceDetectionCamera = ({
  open,
  onClose,
  onCapture,
  loading,
  actionLabel = 'Processing...',
  employeeName = 'Employee',
  profilePhotoUrl = null,
  cameraType = 'login',
}) => {
  const webcamRef = useRef(null);
  const canvasRef = useRef(null);
  const detectTimerRef = useRef(null);
  const hasTriggeredRef = useRef(false);
  const screenshotCanvasRef = useRef(null);

  const [ready, setReady] = useState(false);
  const [faceDetected, setFaceDetected] = useState(false);
  const [confidence, setConfidence] = useState(0);
  const [statusText, setStatusText] = useState('Starting camera...');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  // Always start ready after a short delay - fixes iPhone Safari
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      setReady(true);
      setStatusText('Looking for your face...');
    }, 800);
    return () => clearTimeout(t);
  }, [open]);

  // Reliable screenshot using canvas (works on iPhone)
  const getScreenshot = useCallback(() => {
    const video = webcamRef.current?.video;
    if (!video || video.readyState < 2) return null;

    const canvas = screenshotCanvasRef.current || document.createElement('canvas');
    screenshotCanvasRef.current = canvas;
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    const ctx = canvas.getContext('2d');
    
    try {
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      return canvas.toDataURL('image/jpeg', 0.9);
    } catch (e) {
      return null;
    }
  }, []);

  // Reset on close
  useEffect(() => {
    if (!open) {
      hasTriggeredRef.current = false;
      setReady(false);
      setFaceDetected(false);
      setConfidence(0);
      setStatusText('Starting camera...');
      setProcessing(false);
      setError('');
      if (detectTimerRef.current) clearInterval(detectTimerRef.current);
    }
  }, [open]);

  // Detection loop
  useEffect(() => {
    if (!open || !ready || hasTriggeredRef.current || processing) {
      if (detectTimerRef.current) clearInterval(detectTimerRef.current);
      return;
    }

    const video = webcamRef.current?.video;
    if (!video || video.readyState < 2) {
      detectTimerRef.current = setTimeout(() => {}, DETECT_INTERVAL_MS);
      return;
    }

    const offCanvas = document.createElement('canvas');
    offCanvas.width = video.videoWidth || 640;
    offCanvas.height = video.videoHeight || 480;
    const offCtx = offCanvas.getContext('2d');

    detectTimerRef.current = setInterval(async () => {
      if (!video || hasTriggeredRef.current || processing) return;

      try {
        offCtx.drawImage(video, 0, 0, offCanvas.width, offCanvas.height);
        const result = detectFaceInCanvas(offCanvas);

        const canvas = canvasRef.current;
        if (canvas) {
          const displaySize = { width: video.videoWidth, height: video.videoHeight };
          if (canvas.width !== displaySize.width) canvas.width = displaySize.width;
          if (canvas.height !== displaySize.height) canvas.height = displaySize.height;
          const ctx = canvas.getContext('2d');
          ctx.clearRect(0, 0, canvas.width, canvas.height);

          if (result.hasFace) {
            setFaceDetected(true);
            setConfidence(result.confidence);
            const pct = Math.round(result.confidence * 100);
            const firstName = employeeName.split(' ')[0];
            setStatusText(`Face detected (${pct}%) - ${firstName}`);

            ctx.strokeStyle = '#2196F3';
            ctx.lineWidth = 3;
            ctx.strokeRect(
              result.centerX - result.boxWidth / 2,
              result.centerY - result.boxHeight / 2,
              result.boxWidth,
              result.boxHeight
            );
          } else {
            setFaceDetected(false);
            setConfidence(0);
            setStatusText('No face detected - look at camera');
          }
        }
      } catch (err) {
      }
    }, DETECT_INTERVAL_MS);

    return () => {
      if (detectTimerRef.current) clearInterval(detectTimerRef.current);
    };
  }, [open, ready, processing, employeeName]);

  const handleUserMedia = (stream) => {
    setReady(true);
    setStatusText('Looking for your face...');
  };

  const submitSelfie = useCallback(async (selfieBase64) => {
    setProcessing(true);
    setError('');

    if (!selfieBase64) {
      setError('Unable to capture image. Please allow camera access and try again.');
      setProcessing(false);
      hasTriggeredRef.current = false;
      return;
    }

    try {
      if (onCapture) await onCapture(selfieBase64);
      setProcessing(false);
      hasTriggeredRef.current = false;
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Face verification failed');
      setProcessing(false);
      hasTriggeredRef.current = false;
    }
  }, [onCapture]);

  // Auto-trigger on face detection
  useEffect(() => {
    if (faceDetected && ready && !processing && !hasTriggeredRef.current) {
      hasTriggeredRef.current = true;
      if (detectTimerRef.current) clearInterval(detectTimerRef.current);
      
      const selfie = getScreenshot();
      submitSelfie(selfie);
    }
  }, [faceDetected, ready, processing, submitSelfie, getScreenshot]);

  const handleManualProceed = () => {
    if (hasTriggeredRef.current || processing) return;
    hasTriggeredRef.current = true;
    const selfie = getScreenshot();
    if (selfie) {
      if (detectTimerRef.current) clearInterval(detectTimerRef.current);
      submitSelfie(selfie);
    } else {
      hasTriggeredRef.current = false;
    }
  };

  const getStatusColor = () => {
    if (processing) return '#66BB6A';
    if (error) return '#EF5350';
    if (faceDetected) return '#2196F3';
    return '#EF5350';
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ 
        color: '#D4AF37', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1,
        borderBottom: '1px solid rgba(212,175,55,0.1)'
      }}>
        <Fingerprint sx={{ color: '#D4AF37' }} />
        Face Verification - {employeeName}
        <Chip label="Required" size="small" sx={{ ml: 'auto', color: '#66BB6A', border: '1px solid #66BB6A' }} />
      </DialogTitle>
      <DialogContent sx={{ pt: 2, pb: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Box sx={{ 
            position: 'relative', width: '100%', borderRadius: 2, overflow: 'hidden',
            border: `2px solid ${getStatusColor()}80`,
          }}>
            <Webcam
              ref={webcamRef}
              screenshotFormat="image/jpeg"
              width="100%"
              videoConstraints={{ facingMode: 'user', width: 640, height: 480 }}
              className="webcam-full"
              mirrored={false}
              screenshotQuality={0.9}
              onUserMedia={handleUserMedia}
            />
            <canvas
              ref={canvasRef}
              className="webcam-canvas"
            />

            {!ready && (
              <Box sx={{
                position: 'absolute', inset: 0,
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', background: 'rgba(0,0,0,0.7)', gap: 2,
              }}>
                <CircularProgress sx={{ color: '#D4AF37' }} />
                <Typography sx={{ color: '#D4AF37' }}>{statusText}</Typography>
              </Box>
            )}

            {processing && (
              <Box sx={{
                position: 'absolute', inset: 0,
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', background: 'rgba(0,0,0,0.8)', gap: 2,
              }}>
                <CircularProgress sx={{ color: '#66BB6A' }} size={60} />
                <Typography sx={{ color: '#66BB6A', fontWeight: 700 }}>Verifying face...</Typography>
              </Box>
            )}
          </Box>

          {error && (
            <Alert severity="error" sx={{ mt: 1.5, width: '100%' }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Box sx={{ 
            mt: 1.5, width: '100%', p: 1.5, borderRadius: 2,
            bgcolor: `${getStatusColor()}15`,
            border: `1px solid ${getStatusColor()}40`,
          }}>
            <Typography sx={{ color: getStatusColor(), fontWeight: 600, textAlign: 'center' }}>
              {processing ? 'Processing...' : statusText}
            </Typography>
            {faceDetected && !processing && (
              <Typography variant="caption" sx={{ display: 'block', color: '#B0BEC5', textAlign: 'center', mt: 0.5 }}>
                Detection confidence: {Math.round(confidence * 100)}%
              </Typography>
            )}
          </Box>

          {faceDetected && !processing && (
            <Typography variant="caption" sx={{ color: '#66BB6A', mt: 1 }}>
              Face found! Verifying...
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 1.5, borderTop: '1px solid rgba(212,175,55,0.1)' }}>
        <Button onClick={onClose} sx={{ color: '#B0BEC5' }}>Cancel</Button>
        <Button
          onClick={handleManualProceed}
          variant="contained"
          disabled={loading || processing}
          sx={{ fontWeight: 700 }}
        >
          {loading || processing ? 'Processing...' : 'Manual Proceed'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FaceDetectionCamera;
